import {Button, Input} from "@city-sage/aurum-avis-labs-components";
import { useRepository } from "@city-sage/aurum-avis-labs-components";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "../../lib/QueryKeys";
import { useTranslation } from 'react-i18next';
import { ItineraryGrid } from "../ItineraryGrid";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Search, MapPin, Globe, Calendar, Heart, Compass } from "lucide-react";
import PublicStopsMap, { ZoomMode } from "../main-map/PublicStopsMap";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useDebouncedCallback } from "../../lib/hooks/useDebounce";
import { ItineraryVisibilityEnum, Stop, Itinerary, ItineraryProjection, StopProjection } from "@city-sage/core-client";
import { LocationCard } from "../main-map/LocationCard";
import { Image } from "@city-sage/aurum-avis-labs-components";

import { Avatar } from "../Avatar";
import { useReactions } from "../../lib/useReactions";

import { StopPreviewSheet, AggregatedStopProjection } from "../main-map/StopPreviewSheet";
import { WelcomeModal } from "../WelcomeModal";

interface MapBounds {
  southWest: { lng: number; lat: number };
  northEast: { lng: number; lat: number };
}

interface LocationData {
  location: string;
  itineraryCount: number;
  itineraries: ItineraryProjection[];
  countryImageUrl: string | null;
}

// Interface for mini itinerary card - updated to use ItineraryProjection
interface MiniItineraryCardProps {
  itinerary: ItineraryProjection;
  onClick?: () => void;
}

// Mini itinerary card component for the lower zoom level
const MiniItineraryCard: React.FC<MiniItineraryCardProps> = ({ itinerary, onClick }) => {

  return (
    <motion.div
      className="flex-shrink-0 overflow-hidden rounded-xl"
    >
      <motion.div
        className="w-32 aspect-[2/3] rounded-xl overflow-hidden relative cursor-pointer transition-all duration-300 bg-white shadow-md"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
      >
        {/* Background Image */}
        {itinerary.imageUrl ? (
          <Image
            src={itinerary.imageUrl}
            alt={itinerary.title}
            className="absolute inset-0"
            imageClassName="w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-600" />
        )}

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/20" />

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <div className="space-y-2">
            <h3 className="text-white font-semibold text-sm leading-tight line-clamp-2">{itinerary.title}</h3>

            {/* Stats */}
            <div className="flex items-center gap-3 text-xs text-white/80">
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>{itinerary.daysCount}d</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span>{itinerary.stopsCount}</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="h-3 w-3" />
                <span>{itinerary.likeCount}</span>
              </div>
            </div>

            {/* Author */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/90 truncate">
                @{itinerary.accountUserName || 'Anonymous'}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ExploreCard component
interface ExploreCardProps {
  onClick?: () => void;
}

const ExploreCard: React.FC<ExploreCardProps> = ({ onClick }) => {
  return (
    <motion.div
      className="flex-shrink-0 overflow-hidden rounded-xl"
    >
      <motion.div
        className="w-32 aspect-[2/3] rounded-xl overflow-hidden relative cursor-pointer transition-all duration-300"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={onClick}
      >
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 via-blue-500 to-indigo-600 rounded-xl" />

        {/* Overlay */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-b from-black/10 via-black/5 to-black/30" />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-3">
          <div className="flex-1 flex items-center justify-center">
            <Compass className="h-8 w-8 text-white" />
          </div>
          <div className="text-center">
            <h3 className="text-white font-semibold text-sm leading-tight">Get inspired</h3>
            <p className="text-white/90 text-xs mt-1">Explore tours</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};



export default function LandingPage() {
  const { api } = useRepository();
  const navigate = useNavigate({ from: '/' });
  const search = useSearch({ from: '/' });

  const [currentMode, setCurrentMode] = useState<ZoomMode>('country');
  const [zoomToBounds, setZoomToBounds] = useState<((bounds: MapBounds) => void) | null>(null);
  const [selectedAggregatedStop, setSelectedAggregatedStop] = useState<AggregatedStopProjection | null>(null);
  const [isStopPreviewOpen, setIsStopPreviewOpen] = useState(false);

  // State for filtered itineraries from the map
  const [filteredItineraries, setFilteredItineraries] = useState<ItineraryProjection[]>([]);

  // Current bounds state
  const [currentBounds, setCurrentBounds] = useState<MapBounds | null>(null);

  // Parse bounds from URL search params
  const urlBounds = useMemo(() => {
    if (search.bounds) {
      try {
        return JSON.parse(search.bounds) as MapBounds;
      } catch (error) {
        console.warn('Failed to parse bounds from URL:', error);
        return null;
      }
    }
    return null;
  }, [search.bounds]);

  // Initialize current bounds from URL on mount
  useEffect(() => {
    if (urlBounds && !currentBounds) {
      setCurrentBounds(urlBounds);
    }
  }, [urlBounds, currentBounds]);


  // Debounced function to update URL with bounds
  const debouncedUpdateBounds = useDebouncedCallback((bounds: MapBounds) => {
    navigate({
      search: {
        ...search,
        bounds: JSON.stringify(bounds)
      },
      replace: true
    });
  }, 1000);

  // Helper function to navigate to reel
  const navigateToReel = useCallback((itineraryId: string) => {
    navigate({
      to: '/itineraries/reels/$startId',
      params: { startId: itineraryId },
      search: {
        activeIndex: 0, // Start at first story when clicking from map
        bounds: currentBounds ? JSON.stringify(currentBounds) : undefined
      }
    });
  }, [navigate, currentBounds]);

  // Callback to handle map ready
  const handleMapReady = useCallback((zoomFunction: (bounds: MapBounds) => void) => {
    setZoomToBounds(() => zoomFunction);
  }, []);

  // Callback to handle bounds changes from the map
  const handleBoundsChange = useCallback((bounds: MapBounds) => {
    setCurrentBounds(bounds);
    debouncedUpdateBounds(bounds);
  }, [debouncedUpdateBounds]);

  // Callback to handle filtered itineraries changes from the map
  const handleFilteredItinerariesChange = useCallback((itineraries: ItineraryProjection[]) => {
    setFilteredItineraries(itineraries);
  }, []);

  // Fetch all itinerary projections - much more efficient than fetching full itineraries
  const { data: allItineraries, isLoading: isLoadingItineraries } = useQuery({
    queryKey: ['itinerary-projections'],
    queryFn: () => api.findAllItineraryProjections(),
  });

  // Fetch all stop projections - much more efficient than fetching all stops
  const { data: allStops } = useQuery({
    queryKey: ['stop-projections'],
    queryFn: () => api.findAllStopProjections(),
  });

  // Determine what to show based on current mode and zoom level
  const displayData = useMemo(() => {
    if (!allStops || allStops.length === 0 || !filteredItineraries) return { type: 'countries', items: [] };

    // At country level (high zoom), show countries
    if (currentMode === 'country') {
      const locationMap = new Map<string, LocationData>();

      // Aggregate by country
      allStops.forEach((stopProjection: StopProjection) => {
        if (!stopProjection.placeAddressCountryName) return;

        const country = stopProjection.placeAddressCountryName;

        // Find the specific itinerary for this stop
        const itinerary = filteredItineraries.find(it => it.id === stopProjection.itineraryId);
        if (!itinerary) return;

        if (locationMap.has(country)) {
          const existing = locationMap.get(country)!;
          // Add itinerary if not already present
          if (!existing.itineraries.some((i: ItineraryProjection) => i.id === itinerary.id)) {
            existing.itineraries.push(itinerary);
            existing.itineraryCount = existing.itineraries.length;
          }
        } else {
          locationMap.set(country, {
            location: country,
            itineraryCount: 1,
            itineraries: [itinerary],
            countryImageUrl: itinerary.countryImageUrl
          });
        }
      });

      return { type: 'countries', items: Array.from(locationMap.values()).sort((a, b) => a.location.localeCompare(b.location)) };
    } else {
      // At city level (low zoom), show individual itineraries
      return { type: 'itineraries', items: filteredItineraries };
    }
  }, [allStops, currentMode, filteredItineraries]);

  // Handle zoom mode changes
  const handleZoomChange = (mode: ZoomMode) => {
    setCurrentMode(mode);
  };

  // Handle stop marker click
  const handleStopClick = useCallback((aggregatedStop: AggregatedStopProjection) => {
    setSelectedAggregatedStop(aggregatedStop);
    setIsStopPreviewOpen(true);
  }, []);

  // Handle stop preview sheet close
  const handleStopPreviewClose = useCallback(() => {
    setIsStopPreviewOpen(false);
    setSelectedAggregatedStop(null);
  }, []);

  const handleLocationClick = (locationData: LocationData) => {
    // Calculate bounds for all stops in this country
    if (!allStops || allStops.length === 0) return;

    const countryStops = allStops.filter(stopProjection =>
      stopProjection.placeAddressCountryName === locationData.location
    );

    if (countryStops.length === 0) return;

    // Calculate the bounding box
    const lats = countryStops.map(stopProjection => stopProjection.placeCoordinatesLatitude).filter(Boolean) as number[];
    const lngs = countryStops.map(stopProjection => stopProjection.placeCoordinatesLongitude).filter(Boolean) as number[];

    if (lats.length === 0 || lngs.length === 0) return;

    const bounds = {
      southWest: {
        lat: Math.min(...lats),
        lng: Math.min(...lngs)
      },
      northEast: {
        lat: Math.max(...lats),
        lng: Math.max(...lngs)
      }
    };

    // Add some padding to the bounds
    const latPadding = (bounds.northEast.lat - bounds.southWest.lat) * 0.1;
    const lngPadding = (bounds.northEast.lng - bounds.southWest.lng) * 0.1;

    const paddedBounds = {
      southWest: {
        lat: bounds.southWest.lat - latPadding,
        lng: bounds.southWest.lng - lngPadding
      },
      northEast: {
        lat: bounds.northEast.lat + latPadding,
        lng: bounds.northEast.lng + lngPadding
      }
    };

    // Zoom to these bounds
    if (zoomToBounds) {
      zoomToBounds(paddedBounds);
    }
  };
  // Handle explore stories action for welcome modal
  const handleExploreStories = useCallback(() => {
    // Navigate to the first itinerary if available
    if (allItineraries && allItineraries.length > 0) {
      navigateToReel(allItineraries[0].id);
    }
  }, [allItineraries, navigateToReel]);


  return (
    <main className="flex-grow flex flex-col relative">

      {/* Map Section with Location Cards */}
      <section className="w-full relative flex-grow">
        <PublicStopsMap
          stops={allStops}
          itineraries={allItineraries}
          onZoomChange={handleZoomChange}
          onMapReady={handleMapReady}
          onStopClick={handleStopClick}
          onFilteredItinerariesChange={handleFilteredItinerariesChange}
          onBoundsChange={handleBoundsChange}
          initialBounds={urlBounds || undefined}
        />

        {/* Cards Section - Overlapping from bottom */}
        <AnimatePresence mode="wait">
          {displayData.items.length > 0 && (
            <motion.section
              key={`${displayData.type}-cards`}
              className="absolute -bottom-4 left-0 right-0 z-30 max-w-fit"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >

            {/* Cards Container */}
            <div className="relative pb-8 pr-4">
              <div
                className="overflow-x-auto [&::-webkit-scrollbar]:hidden"
                style={{
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                <motion.div
                  className="flex gap-4 px-4 pb-2"
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: {
                      opacity: 1,
                      transition: {
                        staggerChildren: 0.1,
                        delayChildren: 0.2
                      }
                    }
                  }}
                >
                  {/* Always show ExploreCard first */}
                  <motion.div
                    key="explore-card"
                    variants={{
                      hidden: {
                        opacity: 0,
                        x: -20,
                        scale: 0.9
                      },
                      visible: {
                        opacity: 1,
                        x: 0,
                        scale: 1,
                        transition: {
                          type: "spring",
                          stiffness: 300,
                          damping: 30
                        }
                      }
                    }}
                  >
                    <ExploreCard
                      onClick={() => {
                        // Navigate to the first itinerary if available
                        if (allItineraries && allItineraries.length > 0) {
                          navigateToReel(allItineraries[0].id);
                        }
                      }}
                    />
                  </motion.div>

                  {displayData.type === 'countries' ? (
                    // Show country location cards
                    (displayData.items as LocationData[]).map((locationData, index) => (
                      <motion.div
                        key={locationData.location}
                        variants={{
                          hidden: {
                            opacity: 0,
                            x: -20,
                            scale: 0.9
                          },
                          visible: {
                            opacity: 1,
                            x: 0,
                            scale: 1,
                            transition: {
                              type: "spring",
                              stiffness: 300,
                              damping: 30
                            }
                          }
                        }}
                      >
                        <LocationCard
                          location={locationData.location}
                          itineraryCount={locationData.itineraryCount}
                          onClick={() => handleLocationClick(locationData)}
                          mode={currentMode}
                          countryImageUrl={locationData.countryImageUrl}
                        />
                      </motion.div>
                    ))
                  ) : (
                    // Show individual itinerary cards
                    (displayData.items as ItineraryProjection[]).map((itinerary, index) => (
                      <motion.div
                        key={itinerary.id}
                        variants={{
                          hidden: {
                            opacity: 0,
                            x: -20,
                            scale: 0.9
                          },
                          visible: {
                            opacity: 1,
                            x: 0,
                            scale: 1,
                            transition: {
                              type: "spring",
                              stiffness: 300,
                              damping: 30
                            }
                          }
                        }}
                      >
                        <div
                          onClick={() => {
                            navigateToReel(itinerary.id);
                          }}
                        >
                          <MiniItineraryCard
                            itinerary={itinerary}
                          />
                        </div>
                      </motion.div>
                    ))
                  )}
                </motion.div>
              </div>
            </div>
            </motion.section>
          )}
        </AnimatePresence>
      </section>

      {/* Stop Preview Sheet */}
      <StopPreviewSheet
        aggregatedStop={selectedAggregatedStop}
        isOpen={isStopPreviewOpen}
        onClose={handleStopPreviewClose}
      />

      {/* Welcome Modal */}
      <WelcomeModal
        onExploreStories={handleExploreStories}
      />
    </main>
  );
}
